import { Injectable } from "@angular/core";
import { Observable, from, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { AttachmentDownloadInfo } from "src/app/models/attachment-download-info";
import { saveFileInUserSystem } from "src/app/UtilFunctions/file-utils";

/**
 * Shared attachment fetcher for the S3 download contract (PT-16085 F1).
 *
 * Every attachment download endpoint now returns {fileName, fileType, fileSize, downloadUrl}
 * instead of raw bytes. This service is the single place that turns that metadata into a Blob:
 *
 *  - `downloadUrl` present  -> plain GET on the presigned S3 URL using native `fetch()`. The
 *    interceptor chain is deliberately bypassed: AuthInterceptor attaches custom headers
 *    (Authorization, timeZone, screenName) to every HttpClient request, and any custom header on
 *    an S3 request breaks it (CORS preflight / signature mismatch). A 403 means the URL expired
 *    (~10 min TTL) — when the caller supplies `refreshInfo$`, the metadata is re-fetched once and
 *    the download retried transparently.
 *  - `downloadUrl` null     -> the file is a legacy database row; the caller-supplied `stream-*`
 *    fallback (a normal authenticated HttpClient blob request) is used instead. Legacy handling
 *    lives ONLY here, so it can be deleted in one place once the backend data migration removes
 *    the stream endpoints (backend P16).
 */
@Injectable({ providedIn: "root" })
export class AttachmentDownloadService {
    /**
     * Resolves the attachment bytes for the given download metadata.
     *
     * @param info            the endpoint's `data` payload
     * @param legacyFallback$ factory for the endpoint's `stream-*` blob request; used only when
     *                        `info.downloadUrl` is null
     * @param refreshInfo$    optional factory that re-fetches fresh download metadata; used once
     *                        when the presigned URL has expired (HTTP 403)
     */
    fetchBlob(
        info: AttachmentDownloadInfo,
        legacyFallback$: () => Observable<Blob>,
        refreshInfo$?: () => Observable<AttachmentDownloadInfo>,
    ): Observable<Blob> {
        if (!info.downloadUrl) {
            return legacyFallback$();
        }
        return this.fetchPresignedUrl(info.downloadUrl).pipe(
            catchError((error) => {
                // 403 = expired/invalid presigned URL: refresh the metadata once and retry
                if (error instanceof PresignedUrlError && error.status === 403 && refreshInfo$) {
                    return refreshInfo$().pipe(
                        switchMap((freshInfo) =>
                            freshInfo.downloadUrl ? this.fetchPresignedUrl(freshInfo.downloadUrl) : legacyFallback$(),
                        ),
                    );
                }
                return throwError(() => error);
            }),
        );
    }

    /** Fetches the attachment (S3 or legacy fallback) and saves it with its original file name. */
    downloadToDevice(
        info: AttachmentDownloadInfo,
        legacyFallback$: () => Observable<Blob>,
        refreshInfo$?: () => Observable<AttachmentDownloadInfo>,
    ): Observable<void> {
        return this.fetchBlob(info, legacyFallback$, refreshInfo$).pipe(
            map((blob) => saveFileInUserSystem(blob, info.fileName)),
        );
    }

    /**
     * Plain GET on a presigned URL. No headers of any kind (PT-16085 golden rule G1): the
     * authorization is inside the URL itself, and extra headers break the S3 request.
     */
    private fetchPresignedUrl(url: string): Observable<Blob> {
        return from(fetch(url)).pipe(
            switchMap((response) => {
                if (!response.ok) {
                    return throwError(() => new PresignedUrlError(response.status));
                }
                return from(response.blob());
            }),
        );
    }
}

/** Error thrown when the presigned S3 request itself fails (status carries the HTTP code). */
export class PresignedUrlError extends Error {
    constructor(public readonly status: number) {
        super(`Presigned S3 request failed with HTTP ${status}`);
        this.name = "PresignedUrlError";
    }
}